/**
 * Who is being counted, and what that count may be called. RC-9 / B6.
 *
 * TWO DEFECTS, ONE CAUSE. Ten surfaces labelled a count "sales officers" while
 * counting Sales Managers, Admin Managers and the viewer as well — the field
 * map said "N sales officers in scope" over a set that included the manager
 * reading it, and the team-attendance alert said "N sales officers haven't
 * checked in yet" over a list the manager himself could appear in. Separately,
 * three incompatible formulas for one population coexisted — reports only,
 * reports + self, reports + self unfiltered — which is how one profile showed
 * 5, 5 and 6 for the same team.
 *
 * THE RULE (B6, and it reversed an earlier one): A TEAM INCLUDES ITS MANAGER.
 * One inclusive rule everywhere. Because that is surprising to anyone who
 * thinks "team" means "my reports", an inclusive count must SHOW ITS SPLIT
 * rather than assert a total: "4 officers + you", never a bare "5".
 *
 * PROMOTED FROM: the Team page role breakdown (F456) for the role counting,
 * and the Assistant Manager dashboard for the split wording — it already read
 * `team_reports` from the backend and rendered "N officers + you". The backend
 * ships `team_size`, `team_reports` and `team_includes_manager` together for
 * exactly this reason, and a surface that has them must not recompute them.
 *
 * A LABEL IS A CLAIM. `labelFor` exists so no surface can say "sales officers"
 * over a mixed population again: if the set holds more than one role, the only
 * honest words are the general ones.
 */

// THE ROLE WORDS COME FROM `roleOptions`, WHICH ALREADY HELD THEM.
//
// This file shipped its own `ROLE_LABEL` for one day and that was a defect of
// exactly the kind Block 2 exists to remove: `roleOptions` says "Sales
// Manager", the copy here said "Sales Mgr", and the Team page renders both — a
// badge from one and a breakdown line from the other — so one person was a
// "Sales Manager" in a chip and one of "1 Sales Mgr" in the sentence above it.
// A sweep that adds a sixth copy of a fact has not swept.
//
// Plurals stay here because `roleOptions` has none, and "Sales Officers" is not
// `label + "s"` for every entry.
import { ROLE_LABEL } from "./roleOptions";

export { ROLE_LABEL };

/** Plural forms, keyed to the same labels. */
export const ROLE_LABEL_PLURAL = {
  director: "Directors",
  admin: "Tech Admins",
  admin_manager: "Admin Managers",
  assistant_manager: "Sales Managers",
  sales_officer: "Sales Officers",
};

/** The roles that carry a field book. Mirrors the backend's FIELD_ROLES. */
export const FIELD_ROLES = ["sales_officer", "assistant_manager"];

/** Display order — seniority, so a breakdown always reads the same way. */
const ORDER = ["director", "admin", "admin_manager", "assistant_manager", "sales_officer"];

/**
 * Count a list of user rows by role.
 *
 * Promoted from `TeamPage`'s `counts` useMemo, with one addition: `unknown`.
 * The original `else if` chain silently dropped a row whose role it did not
 * recognise, so the per-role numbers could sum to less than the total with
 * nothing saying so.
 */
export function roleBreakdown(users, { activeOnly = false } = {}) {
  const rows = (users || []).filter((u) => (activeOnly ? u.is_active : true));
  const counts = {
    total: rows.length,
    active: 0,
    director: 0,
    admin: 0,
    admin_manager: 0,
    assistant_manager: 0,
    sales_officer: 0,
    unknown: 0,
  };
  for (const u of rows) {
    if (u.is_active) counts.active += 1;
    if (Object.prototype.hasOwnProperty.call(ROLE_LABEL, u.role)) counts[u.role] += 1;
    else counts.unknown += 1;
  }
  return counts;
}

/** `2 Sales Officers · 1 Sales Mgr` — the roles actually present, in order. */
export function breakdownLabel(counts, { plural = true } = {}) {
  if (!counts) return "";
  const parts = [];
  for (const role of ORDER) {
    const n = counts[role] || 0;
    if (!n) continue;
    const label = n === 1 || !plural ? ROLE_LABEL[role] : ROLE_LABEL_PLURAL[role];
    parts.push(`${n} ${label}`);
  }
  if (counts.unknown) parts.push(`${counts.unknown} role not recognised`);
  return parts.join(" · ");
}

/**
 * The honest noun for a population.
 *
 * One role present -> that role's name. More than one -> "people". Never the
 * name of the largest group, which is the shortcut that produced "sales
 * officers" over a set containing three other roles.
 */
export function labelFor(counts, { plural = true } = {}) {
  if (!counts) return plural ? "people" : "person";
  const present = ORDER.filter((r) => (counts[r] || 0) > 0);
  if (counts.unknown) return plural ? "people" : "person";
  if (present.length === 1) {
    const only = present[0];
    const n = counts[only] || 0;
    return n === 1 || !plural ? ROLE_LABEL[only] : ROLE_LABEL_PLURAL[only];
  }
  return plural ? "people" : "person";
}

/**
 * "4 Sales Officers + you" — an inclusive count that shows its split.
 *
 * `total` and `reports` come from the SERVER together (`team_size`,
 * `team_reports`); this function does no arithmetic on ids and cannot drift
 * from whatever scope the backend applied. When the viewer is not in the set,
 * it says so by simply not claiming they are.
 */
export function teamCountLabel({ total, reports, includesManager, noun = "in your team" }) {
  if (total === null || total === undefined) return null;
  if (!includesManager) return `${total} ${noun}`;
  const r = reports === null || reports === undefined ? Math.max(0, total - 1) : reports;
  return `${r} ${r === 1 ? "report" : "reports"} + you`;
}

/**
 * The one-line summary a scope header shows: what the number counts, in words.
 * `scopeSummary({total: 6, label: "people in scope"})` -> "6 people in scope".
 * Exists so a surface that only has a total from the server states what the
 * total is OF, rather than borrowing a role name it cannot justify.
 */
export function scopeSummary(total, noun) {
  if (total === null || total === undefined) return null;
  return `${total} ${noun}`;
}
